import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Mail,
  MessageSquare,
  ArrowRight,
  Send,
  Clock,
  CircleArrowDown,
} from "lucide-react";
import TopNavbar from "../components/TopNavbar";
import MobileNavbar from "../components/MobileNavbar";
import Footer from "../components/Landing/Footer";
import { useNavigate } from "react-router-dom";

const Support = () => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: {
      scale: 1.02,
      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
      transition: { duration: 0.3 },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
    exit: {
      opacity: 0,
      x: 20,
      transition: { duration: 0.3 },
    },
  };

  return (
    <div>
      <TopNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      {isMenuOpen && (
        <MobileNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      )}
      <motion.div
        className="px-4 pt-32 pb-8 min-h-screen bg-gray-50 md:p-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header Section */}
        <div className="mx-auto mt-24 mb-8 max-w-4xl md:mb-12">
          <div className="flex flex-row-reverse gap-3 justify-between items-center">
            <button
              onClick={() => navigate(-1)}
              className="mb-4 px-4 py-2 bg-[#FF6C6C] text-white rounded-md hover:bg-[#f94343] hover:scale-105 transition-all duration-300"
            >
              <span className="flex gap2">
                <CircleArrowDown className="mr-2 rotate-90" />{" "}
                <span>Go Back</span>
              </span>
            </button>
            <div>
              <motion.h1
                className="mb-3 text-2xl font-bold md:text-4xl md:mb-4 bg-gradient-to-r from-[#FF6C6C] to-pink-500 text-transparent bg-clip-text"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                How Can We Help You?
              </motion.h1>
              <motion.p
                className="text-base text-gray-600 md:text-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Choose your preferred method of contact
              </motion.p>
            </div>
          </div>
        </div>

        {/* Contact Methods */}
        <div className="grid gap-4 mx-auto mb-8 max-w-4xl md:gap-6 md:grid-cols-2 md:mb-12">
          {/* Phone Support Card */}
          <motion.div
            variants={cardVariants}
            whileHover="hover"
            className="p-4 bg-white rounded-2xl shadow-sm cursor-pointer md:p-6"
            onClick={() => setSelectedMethod("phone")}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                selectedMethod === "phone" ? "bg-[#FF6C6C]" : "bg-pink-100"
              }`}
            >
              <Phone
                className={
                  selectedMethod === "phone" ? "text-white" : "text-[#FF6C6C]"
                }
                size={24}
              />
            </div>
            <h3 className="mb-2 text-xl font-semibold">Phone Support</h3>
            <p className="mb-4 text-gray-600">
              Talk to our support team directly
            </p>
            <div className="flex items-center text-[#FF6C6C] font-medium">
              <span>Call now</span>
              <ArrowRight className="ml-2" size={16} />
            </div>
          </motion.div>

          {/* Email Support Card */}
          <motion.div
            variants={cardVariants}
            whileHover="hover"
            className="p-4 bg-white rounded-2xl shadow-sm cursor-pointer md:p-6"
            onClick={() => setSelectedMethod("email")}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                selectedMethod === "email" ? "bg-[#FF6C6C]" : "bg-pink-100"
              }`}
            >
              <Mail
                className={
                  selectedMethod === "email" ? "text-white" : "text-[#FF6C6C]"
                }
                size={24}
              />
            </div>
            <h3 className="mb-2 text-xl font-semibold">Email Support</h3>
            <p className="mb-4 text-gray-600">Send us your questions</p>
            <div className="flex items-center text-[#FF6C6C] font-medium">
              <span>Write to us</span>
              <ArrowRight className="ml-2" size={16} />
            </div>
          </motion.div>
        </div>

        {/* Contact Form Section */}
        <AnimatePresence mode="wait">
          {selectedMethod === "email" && (
            <motion.div
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="p-4 mx-auto max-w-2xl bg-white rounded-2xl shadow-lg md:p-6"
            >
              <h3 className="mb-6 text-xl font-semibold">Send us a message</h3>
              <form className="space-y-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent outline-none transition-all duration-200"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C] focus:border-transparent outline-none transition-all duration-200 h-32 resize-none"
                    placeholder="How can we help you?"
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-[#FF6C6C] text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#ff5252] transition-colors duration-300"
                >
                  <Send size={18} />
                  Send Message
                </motion.button>
              </form>
            </motion.div>
          )}

          {selectedMethod === "phone" && (
            <motion.div
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="p-4 mx-auto max-w-2xl bg-white rounded-2xl shadow-lg md:p-6"
            >
              <div className="text-center">
                <h3 className="mb-6 text-xl font-semibold">
                  Call our support team
                </h3>
                <a
                  href="tel:+1234567890"
                  className="text-2xl font-bold text-[#FF6C6C] mb-4 block hover:text-[#ff5252] transition-colors"
                >
                  +233 (200) 567-890
                </a>
                <div className="flex gap-2 justify-center items-center mb-6 text-gray-600">
                  <Clock size={18} />
                  <span>Available 24/7</span>
                </div>
                <p className="text-gray-600">
                  Our support team is ready to help you with any questions or
                  concerns
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Support Features */}
        <div className="grid gap-4 mx-auto mt-8 max-w-4xl md:grid-cols-3 md:gap-6 md:mt-12">
          {[
            {
              icon: <MessageSquare className="text-[#FF6C6C]" size={24} />,
              title: "Quick Response",
              description: "Get answers to your questions within minutes",
            },
            {
              icon: <Clock className="text-[#FF6C6C]" size={24} />,
              title: "24/7 Support",
              description: "We're here to help you anytime, anywhere",
            },
            {
              icon: <Send className="text-[#FF6C6C]" size={24} />,
              title: "Expert Team",
              description: "Our team is trained to solve your problems",
            },
          ].map((feature, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="p-4 text-center md:p-6"
            >
              <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-pink-100 rounded-full">
                {feature.icon}
              </div>
              <h3 className="mb-2 font-semibold">{feature.title}</h3>
              <p className="text-sm text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
      <Footer />
    </div>
  );
};

export default Support;
